import { useRef, useEffect } from 'react';
import { TARGET_ANGLES } from '@/lib/angle-detector';

interface RotationOverlayProps {
  phase: 'setup' | 'countdown' | 'rotating' | 'complete';
  currentAngle: number;
  capturedAngles: Set<number>;
  countdown: number;
  modelLoading: boolean;
  keyframeCount: number;
  totalTargets: number;
}

/**
 * Canvas overlay for the rotation capture screen.
 * Draws a circular progress ring showing captured angles,
 * a direction indicator, and countdown visuals.
 */
export function RotationOverlay({
  phase,
  currentAngle,
  capturedAngles,
  countdown,
  modelLoading,
  keyframeCount,
  totalTargets,
}: RotationOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const cw = rect.width;
    const ch = rect.height;
    ctx.clearRect(0, 0, cw, ch);

    // ── Countdown overlay ──
    if (phase === 'countdown' && countdown > 0) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, cw, ch);

      ctx.save();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      const fontSize = Math.min(cw * 0.35, 140);
      ctx.font = `bold ${fontSize}px -apple-system, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0, 200, 120, 0.5)';
      ctx.shadowBlur = 30;
      ctx.fillText(String(countdown), cw / 2, ch * 0.42);
      ctx.restore();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.font = '16px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Get ready to rotate...', cw / 2, ch * 0.55);
      return;
    }

    // ── Model loading ──
    if (modelLoading) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillRect(0, 0, cw, ch);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = '14px -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Loading pose detector...', cw / 2, ch / 2);
      return;
    }

    // ── Progress ring (shown during setup, rotating, complete) ──
    if (phase === 'rotating' || phase === 'complete' || phase === 'setup') {
      drawProgressRing(ctx, cw, ch, currentAngle, capturedAngles, phase);
    }

    // ── Completion overlay ──
    if (phase === 'complete') {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(0, 0, cw, ch);

      ctx.save();
      ctx.fillStyle = 'rgba(0, 230, 120, 0.95)';
      ctx.font = `bold ${Math.min(cw * 0.12, 48)}px -apple-system, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('\u2713', cw / 2, ch * 0.4);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.font = 'bold 18px -apple-system, sans-serif';
      ctx.fillText('Scan Complete!', cw / 2, ch * 0.48);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.font = '14px -apple-system, sans-serif';
      ctx.fillText(`${keyframeCount} angles captured`, cw / 2, ch * 0.53);
      ctx.restore();
    }
  }, [phase, currentAngle, capturedAngles, countdown, modelLoading, keyframeCount, totalTargets]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 z-[2]"
      style={{ width: '100%', height: '100%' }}
    />
  );
}

// ─── Drawing helpers ────────────────────────────────────────

function drawProgressRing(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  currentAngle: number,
  capturedAngles: Set<number>,
  phase: string
) {
  const cx = cw / 2;
  const cy = ch * 0.75; // Lower portion of screen
  const radius = Math.min(cw * 0.15, 60);
  const dotRadius = 5;
  const capturedDotRadius = 7;

  // Semi-transparent background circle
  ctx.beginPath();
  ctx.arc(cx, cy, radius + 12, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.fill();

  // Ring track
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.lineWidth = 3;
  ctx.stroke();

  // Progress arc (how much of 360° has been captured)
  if (capturedAngles.size > 0) {
    const progress = capturedAngles.size / TARGET_ANGLES.length;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress);
    ctx.strokeStyle = 'rgba(0, 230, 120, 0.8)';
    ctx.lineWidth = 4;
    ctx.stroke();
  }

  // Target angle dots
  for (const target of TARGET_ANGLES) {
    const rad = ((target - 90) * Math.PI) / 180; // -90 to start from top
    const dx = cx + radius * Math.cos(rad);
    const dy = cy + radius * Math.sin(rad);

    const isCaptured = capturedAngles.has(target);

    ctx.beginPath();
    ctx.arc(dx, dy, isCaptured ? capturedDotRadius : dotRadius, 0, Math.PI * 2);

    if (isCaptured) {
      ctx.fillStyle = 'rgba(0, 230, 120, 0.9)';
    } else {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    }
    ctx.fill();

    // Small check mark on captured dots
    if (isCaptured) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.font = `bold ${capturedDotRadius}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('\u2713', dx, dy);
    }
  }

  // Current angle indicator (rotating pointer)
  if (phase === 'rotating') {
    const pointerRad = ((currentAngle - 90) * Math.PI) / 180;
    const pointerX = cx + (radius + 16) * Math.cos(pointerRad);
    const pointerY = cy + (radius + 16) * Math.sin(pointerRad);

    ctx.beginPath();
    ctx.arc(pointerX, pointerY, 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 180, 0, 0.9)';
    ctx.fill();

    // Line from center to pointer
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(
      cx + (radius - 8) * Math.cos(pointerRad),
      cy + (radius - 8) * Math.sin(pointerRad)
    );
    ctx.strokeStyle = 'rgba(255, 180, 0, 0.5)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  // Center text (angle or count)
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (phase === 'rotating') {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = 'bold 16px -apple-system, sans-serif';
    ctx.fillText(`${Math.round(currentAngle)}°`, cx, cy - 6);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '10px -apple-system, sans-serif';
    ctx.fillText(`${capturedAngles.size}/${TARGET_ANGLES.length}`, cx, cy + 10);
  } else if (phase === 'setup') {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '11px -apple-system, sans-serif';
    ctx.fillText('360°', cx, cy);
  }

  ctx.restore();
}
